import { useMemo, useState } from 'react';
import { analyzeRummyHand, isRummyWild, rankLabel, sortHand, type Rank, type RummyLegal, type RummyView } from '@cardhub/engine';
import { PlayingCard, shortCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { toast } from '../store/ui';
import { MySeat, SeatView, useNarrow, useSpeech } from './common';

export function RummyTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as RummyView;
  const legal = useGame((s) => s.legal) as RummyLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'declare'; groups: string[][]; discard: string } | { kind: 'drop' } | null>(null);
  const total = view.handCounts.length;
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const narrow = useNarrow();
  const wild = (c: string) => isRummyWild(c, view.wildRank);
  const wildLabel = `${rankLabel(view.wildRank as Rank)}s`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const iAmIn = mySeat !== null && view.inHand[mySeat] !== false;
  const lastResult = view.dealResults.at(-1);
  const dealOver = view.phase === 'ROUND_END' || view.phase === 'MATCH_END';

  // Groups first (pure sequences, then sequences, then sets), loose cards after them, so progress is visible.
  const analysis = useMemo(() => (view.hand.length > 0 ? analyzeRummyHand(view.hand, view.wildRank) : null), [view.hand, view.wildRank]);
  const layout = useMemo(() => {
    if (!analysis) return { groups: [] as string[][], rest: [] as string[], points: 0 };
    const arr = analysis.arrangement();
    let points = arr.deadwood;
    // With 14 cards in hand the score that matters is the best one after the discard.
    if (view.hand.length > 13) points = Math.min(...view.hand.map((_, i) => analysis.deadwood(i)));
    return { groups: arr.groups, rest: sortHand(arr.rest), points: Math.min(points, view.config.maxPoints) };
  }, [analysis, view.hand, view.config.maxPoints]);

  const twoRows = view.hand.length > 12;
  const size = twoRows || narrow ? 'md' : 'lg';
  const pileSize = narrow ? 'md' : 'lg';
  const canDiscard = Boolean(myTurn && legal?.canDiscard);

  const pick = (c: string) => {
    if (canDiscard) setSelected(selected === c ? null : c);
  };

  const doDeclare = () => {
    if (!analysis) return;
    const order = selected ? [view.hand.indexOf(selected), ...view.hand.map((_, i) => i)] : view.hand.map((_, i) => i);
    for (const i of order) {
      if (i >= 0 && analysis.isMade(i)) {
        setConfirm({ kind: 'declare', groups: analysis.arrangement(i).groups, discard: view.hand[i]! });
        return;
      }
    }
    toast(t('ru.notReady'), 'error');
  };

  const cards = [
    ...layout.groups.flatMap((g, gi) =>
      g.map((c, k) => (
        <span key={c} style={{ display: 'inline-block', marginRight: k === g.length - 1 ? 10 : 0 }} data-group={gi}>
          <PlayingCard id={c} size={size} wild={wild(c)} selected={selected === c} onClick={() => pick(c)} />
        </span>
      )),
    ),
    ...layout.rest.map((c) => (
      <span key={c} style={{ display: 'inline-block' }}>
        <PlayingCard id={c} size={size} wild={wild(c)} selected={selected === c} onClick={() => pick(c)} />
      </span>
    )),
  ];
  const split = twoRows ? Math.ceil(cards.length / 2) : cards.length;

  const reasonLine = (r: NonNullable<typeof lastResult>) =>
    r.reason === 'DECLARE' && r.winner !== null
      ? t('ru.winner', { name: name(r.winner) })
      : r.reason === 'LAST_PLAYER' && r.winner !== null
        ? t('ru.lastPlayer', { name: name(r.winner) })
        : t('ru.stockOut');

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {Array.from({ length: total }, (_, s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={total}
            turn={turn}
            speech={speech[s]}
            folded={view.inHand[s] === false}
            sub={<span className="dim num">{view.totals[s] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {view.inHand[s] === false && !dealOver && <span className="tag">{t('ru.dropped')}</span>}
                {view.inHand[s] !== false && !dealOver && <span className="tag">{view.handCounts[s]}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('ru.deal', { n: view.deal, total: view.config.deals })} · {t('ru.wild', { rank: wildLabel })}
          </div>
          {dealOver && lastResult ? (
            <div className="card glass" style={{ textAlign: 'center' }} role="status">
              <strong>{reasonLine(lastResult)}</strong>
              <div className="row" style={{ justifyContent: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                {lastResult.points.map((p, s) => (
                  <span key={s} className={`pill ${p === 0 ? 'accent' : ''}`}>
                    {name(s)}: {p}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="row" style={{ gap: 14, alignItems: 'flex-end', justifyContent: 'center' }}>
              <button className="col" style={{ alignItems: 'center', gap: 4, background: 'none', border: 0 }} disabled={!myTurn || !legal?.canDrawStock || acting} onClick={() => void act({ type: 'draw', from: 'stock' })} aria-label={t('mr.drawStock')}>
                <PlayingCard faceDown size={pileSize} className={myTurn && legal?.canDrawStock ? 'playable' : ''} />
                <span className="center-note">{t('mr.stock', { n: view.stockCount })}</span>
              </button>
              <button className="col" style={{ alignItems: 'center', gap: 4, background: 'none', border: 0 }} disabled={!myTurn || !legal?.canDrawDiscard || acting} onClick={() => void act({ type: 'draw', from: 'discard' })} aria-label={t('mr.pickDiscard')}>
                {view.discardTop ? <PlayingCard id={view.discardTop} size={pileSize} wild={wild(view.discardTop)} className={myTurn && legal?.canDrawDiscard ? 'playable' : ''} /> : <span className={`pcard ${pileSize}`} />}
                <span className="center-note">{t('mr.pickDiscard')}</span>
              </button>
              <div className="col" style={{ alignItems: 'center', gap: 4 }} aria-label={t('ru.cutJoker')}>
                <PlayingCard id={view.cutJoker} size={narrow ? 'sm' : 'md'} wild />
                <span className="center-note" style={{ fontSize: 'var(--fs-xs)' }}>{t('ru.cutJoker')}</span>
              </div>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.totals[mySeat] ?? 0} pts{iAmIn && !dealOver && view.hand.length > 0 ? ` · ${t('ru.points', { n: layout.points })}` : ''}</> : null} />
      </div>
      <div className="table-bottom">
        <div role="group" aria-label="Your hand" className="hand-rows">
          <Hand size={size} label={twoRows ? 'Your hand, row 1' : 'Your hand'} maxStep={0.6}>
            {cards.slice(0, split)}
          </Hand>
          {twoRows && (
            <Hand size={size} label="Your hand, row 2" maxStep={0.6}>
              {cards.slice(split)}
            </Hand>
          )}
        </div>
        <div className="action-bar">
          {myTurn && legal?.canDrop && (
            <button className="btn ghost sm" disabled={acting} onClick={() => setConfirm({ kind: 'drop' })}>
              {t('ru.drop')}
            </button>
          )}
          {canDiscard && (
            <button className="btn primary" disabled={!selected || acting} onClick={() => selected && void act({ type: 'discard', card: selected }).then((ok) => ok && setSelected(null))}>
              {t('mr.discard')}
              {selected ? ` ${shortCard(selected)}` : ''}
            </button>
          )}
          {myTurn && legal?.canFinish && (
            <button className="btn gold" disabled={acting} onClick={doDeclare}>
              {t('ru.declare')}
            </button>
          )}
        </div>
      </div>
      <Modal open={Boolean(confirm)} onClose={() => setConfirm(null)} title={confirm?.kind === 'drop' ? t('ru.dropTitle') : t('ru.declareTitle')}>
        {confirm?.kind === 'declare' && (
          <>
            <div className="melds" style={{ marginBottom: 12 }}>
              {confirm.groups.map((g, i) => (
                <div key={i} className="meld">
                  {g.map((c) => (
                    <PlayingCard key={c} id={c} size="md" wild={wild(c)} />
                  ))}
                </div>
              ))}
            </div>
            <p className="dim">{t('ru.declareBody', { card: shortCard(confirm.discard) })}</p>
          </>
        )}
        {confirm?.kind === 'drop' && <p className="dim">{t('ru.dropBody', { n: legal?.dropPoints ?? 0 })}</p>}
        <div className="row">
          <button className="btn ghost grow" onClick={() => setConfirm(null)}>
            {t('common.cancel')}
          </button>
          <button
            className="btn primary grow"
            onClick={() => {
              const c = confirm;
              setConfirm(null);
              if (c?.kind === 'declare') void act({ type: 'finish', discard: c.discard }).then((ok) => ok && setSelected(null));
              else if (c?.kind === 'drop') void act({ type: 'drop' });
            }}
          >
            {confirm?.kind === 'drop' ? t('ru.drop') : t('ru.declare')}
          </button>
        </div>
      </Modal>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {Array.from({ length: total }, (_, s) => (
                <th key={s} className="ellipsis" style={{ maxWidth: 70 }}>{name(s)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.dealResults.map((r) => (
              <tr key={r.deal}>
                <td>D{r.deal}</td>
                {r.points.map((p, s) => (
                  <td key={s} className="num" style={{ color: p === 0 ? 'var(--accent)' : undefined }}>
                    {p}
                    {r.drops[s] && <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('ru.dropped')}</div>}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.totals.map((v, s) => (
                <td key={s} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('ru.scoreHint', { max: view.config.maxPoints })}</p>
      </Modal>
    </>
  );
}
